<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import { VModal, VTaxIdField, VSection, VButton } from '@/modules/shared/components'
import TheUploader from '@/modules/shared/components/the-uploader.vue'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useFilingStore } from '@/modules/filing/stores/filing-store'
import { useEntityStore } from '@/modules/investing/stores/better-entity-store'
import { useTaskListStore } from '@/modules/tasking/stores/tasking-store'
import { type Task } from '@/modules/tasking/types'

const props = defineProps<{
  task: Task | null
  spvId: string
  slug: string
  filingFolderRootId?: string | null
  currentEin?: string | null
}>()

const modal = useModal()
defineExpose({ modal })

const entityStore = useEntityStore()
const filingStore = useFilingStore()
const taskListStore = useTaskListStore()

const form = ref({ ein: '' })
const files = ref<File[]>([])
const isSubmitting = ref(false)

const ACCEPTED_FILE_TYPES = ['.pdf', '.png', '.jpg', '.jpeg', '.doc', '.docx']

// Matches the digit-counting approach used in individual-form.vue and
// use-spv-form.ts — accepts any separator style (12-3456789, 123456789, etc.)
// since the backend's `apply_tax_id_format` reformats to XX-XXXXXXX before save.
const rules = {
  ein: {
    hasNineDigits: helpers.withMessage(
      'EIN must contain exactly 9 digits',
      (value: string) => !value || value.replace(/\D/g, '').length === 9,
    ),
  },
}
const v$ = useVuelidate(rules, form, { $lazy: true })

const canSubmit = computed(() => form.value.ein.trim().length > 0 || files.value.length > 0)

const onFilesChosen = (chosen: File[] | File) => {
  files.value = Array.isArray(chosen) ? chosen : [chosen]
}

const reset = () => {
  form.value.ein = props.currentEin ?? ''
  files.value = []
  v$.value.$reset()
}

// Pre-fill the EIN every time the modal opens, so the user sees the
// stored value (and can edit it) instead of an empty field.
watch(
  () => modal.state.active,
  (isOpen) => {
    if (isOpen) {
      form.value.ein = props.currentEin ?? ''
      files.value = []
      v$.value.$reset()
    }
  },
)

const close = () => {
  reset()
  modal.close()
}

const submit = async () => {
  if (!canSubmit.value || isSubmitting.value) return

  if (form.value.ein.trim()) {
    const valid = await v$.value.$validate()
    if (!valid) return
  }

  isSubmitting.value = true
  try {
    if (form.value.ein.trim()) {
      await entityStore.updateTaxId(props.spvId, form.value.ein.trim(), { slug: props.slug })
    }
    if (files.value.length && props.filingFolderRootId && props.task) {
      const taskName = props.task.name
      await Promise.all(
        files.value.map((file) => {
          const prefixed = new File([file], `${taskName} - ${file.name}`, { type: file.type })
          return filingStore.addFile(prefixed, props.filingFolderRootId!, Number(props.spvId), 'InvestorSet' as any)
        }),
      )
    }
    if (props.task) {
      await taskListStore.completeTask(props.task.id)
    }
    close()
  } catch (err) {
    console.error('EIN submission failed:', err)
  } finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <VModal :modalStore="modal">
    <template #main>
      <VSection label="Submit EIN">
        <p class="mt-2 text-sm text-gray-500">
          Enter the Employer Identification Number for the SPV and optionally upload one or more confirmation documents.
        </p>
        <form @submit.prevent="submit">
          <div class="mt-6 space-y-4">
            <VTaxIdField v-model="form.ein" label="EIN" format="ein" property="ein" :v$="v$" />
            <div>
              <label class="block text-sm font-medium text-gray-700">Confirmation documents (optional)</label>
              <div class="mt-1">
                <TheUploader :accept="ACCEPTED_FILE_TYPES" :multiple_files="true" @chosen="onFilesChosen" />
              </div>
            </div>
          </div>
        </form>
      </VSection>
    </template>
    <template #footer>
      <div class="flex items-center justify-between space-x-3">
        <VButton :click="close" size="lg">Cancel</VButton>
        <VButton
          :click="submit"
          class="w-full"
          size="lg"
          :loading="isSubmitting"
          :disabled="!canSubmit || isSubmitting"
          variant="v-green"
        >
          Save
        </VButton>
      </div>
    </template>
  </VModal>
</template>
